import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { MultiSelect } from "../ui/multi-select";
import { Calendar } from "../ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Filter, Calendar as CalendarIcon, Users, Building2, Tag, Search, X, CheckCircle } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { useSelector } from "react-redux";
import { selectLeadStatuses } from "../../store/slices/leadStatusSlice";
import { fetchDesks } from "../../services/crmServices/deskService";
import { searchUsers } from "../../services/crmServices/userService";
import { getFullName } from "../../services/common/util";
import { fetchPositions, searchPositions } from "../../services/crmServices/positionService";

export function BulkFilterModal({ open, onClose, onApplyFilters, allLeads }) {
    const leadStatuses = useSelector(selectLeadStatuses);
    const [filters, setFilters] = useState({
        statuses: [],
        desks: [],
        agents: [],
        affiliates: [],
        sources: [],
        countries: [],
        dateFrom: null,
        dateTo: null
    });

    const [availableOptions, setAvailableOptions] = useState({
        desks: [],
        agents: [],
        affiliates: [],
        sources: [],
        countries: []
    });

    const [filteredCount, setFilteredCount] = useState(0);

    useEffect(() => {
        if (open) {
            loadFilterOptions();
        }
    }, [open, allLeads]);

    useEffect(() => {
        // Calculate filtered count whenever filters change
        const count = getFilteredLeadsCount();
        setFilteredCount(count);
    }, [filters, allLeads]);

    const loadFilterOptions = async () => {
        try {
            // Load desks
            const desksResponse = await fetchDesks();
            const desks = desksResponse?.data || [];
            const agentPositionsResponse = await searchPositions({ role: "agent" });
            const agentPositions = agentPositionsResponse?.data || [];
            // Load agents
            const agentsResponse = await searchUsers({ position: { $nin: agentPositions.map((pos) => pos._id) } });
            const agents = agentsResponse?.data || [];

            // Extract unique values from leads
            const affiliates = [...new Set(allLeads.map((lead) => lead.affiliate).filter(Boolean))];
            const sources = [...new Set(allLeads.map((lead) => lead.source).filter(Boolean))];
            const countries = [...new Set(allLeads.map((lead) => lead.country).filter(Boolean))];

            setAvailableOptions({
                desks: desks.map((desk) => ({ label: desk.name, value: desk._id })),
                agents: agents.map((agent) => ({ label: getFullName(agent), value: agent._id })),
                affiliates: affiliates.map((affiliate) => ({ label: affiliate, value: affiliate })),
                sources: sources.map((source) => ({ label: source, value: source })),
                countries: countries.map((country) => ({ label: country, value: country }))
            });
        } catch (error) {
            console.error("Error loading filter options:", error);
            toast.error("Failed to load filter options");
        }
    };

    const getFilteredLeadsCount = () => {
        let filtered = [...allLeads];

        // Apply status filter
        if (filters.statuses.length > 0) {
            filtered = filtered.filter((lead) => filters.statuses.includes(lead.status));
        }

        // Apply desk filter
        if (filters.desks.length > 0) {
            filtered = filtered.filter((lead) => filters.desks.includes(lead.desk?._id));
        }

        // Apply agent filter
        if (filters.agents.length > 0) {
            filtered = filtered.filter((lead) => filters.agents.includes(lead.agent?._id));
        }

        // Apply affiliate filter
        if (filters.affiliates.length > 0) {
            filtered = filtered.filter((lead) => filters.affiliates.includes(lead.affiliate));
        }

        // Apply source filter
        if (filters.sources.length > 0) {
            filtered = filtered.filter((lead) => filters.sources.includes(lead.source));
        }

        // Apply country filter
        if (filters.countries.length > 0) {
            filtered = filtered.filter((lead) => filters.countries.includes(lead.country));
        }

        // Apply date range filter
        if (filters.dateFrom) {
            filtered = filtered.filter((lead) => new Date(lead.createdAt) >= filters.dateFrom);
        }
        if (filters.dateTo) {
            filtered = filtered.filter((lead) => new Date(lead.createdAt) <= filters.dateTo);
        }

        return filtered.length;
    };

    const handleFilterChange = (key, value) => {
        setFilters((prev) => ({
            ...prev,
            [key]: value
        }));
    };

    const handleDateChange = (key, date) => {
        setFilters((prev) => ({
            ...prev,
            [key]: date
        }));
    };

    const handleApplyFilters = () => {
        if (filteredCount === 0) {
            toast.error("No leads match the selected filters");
            return;
        }

        onApplyFilters(filters);
        toast.success(`Applied filters - ${filteredCount} leads match`);
    };

    const handleClearFilters = () => {
        setFilters({
            statuses: [],
            desks: [],
            agents: [],
            affiliates: [],
            sources: [],
            countries: [],
            dateFrom: null,
            dateTo: null
        });
    };

    const getActiveFiltersCount = () => {
        return Object.values(filters).filter((value) => {
            if (Array.isArray(value)) return value.length > 0;
            return value !== null && value !== "";
        }).length;
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center">
                        <Filter className="h-5 w-5 mr-2" />
                        Bulk Filter Leads
                    </DialogTitle>
                    <DialogDescription>
                        Apply filters to select leads for bulk editing. Total leads: {allLeads.length}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-6">
                    {/* Filter Results Summary */}
                    <Card className="bg-blue-50 border-blue-200">
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-2">
                                    <Search className="h-5 w-5 text-blue-600" />
                                    <span className="font-medium text-blue-900">
                                        Filtered Results: {filteredCount} leads
                                    </span>
                                </div>
                                <Badge variant="outline" className="bg-blue-100 text-blue-800">
                                    {getActiveFiltersCount()} active filters
                                </Badge>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Status & Assignment Filters */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center">
                                <Tag className="h-5 w-5 mr-2" />
                                Status & Assignment
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <Label>Lead Status</Label>
                                    <MultiSelect
                                        options={leadStatuses}
                                        selected={filters.statuses}
                                        onChange={(selected) => handleFilterChange("statuses", selected)}
                                        placeholder="Select statuses"
                                        searchPlaceholder="Search statuses..."
                                    />
                                </div>
                                <div>
                                    <Label>Affiliate</Label>
                                    <MultiSelect
                                        options={availableOptions.affiliates}
                                        selected={filters.affiliates}
                                        onChange={(selected) => handleFilterChange("affiliates", selected)}
                                        placeholder="Select affiliates"
                                        searchPlaceholder="Search affiliates..."
                                    />
                                </div>
                                <div>
                                    <Label>Source</Label>
                                    <MultiSelect
                                        options={availableOptions.sources}
                                        selected={filters.sources}
                                        onChange={(selected) => handleFilterChange("sources", selected)}
                                        placeholder="Select sources"
                                        searchPlaceholder="Search sources..."
                                    />
                                </div>
                                <div>
                                    <Label>Desk</Label>
                                    <MultiSelect
                                        options={availableOptions.desks}
                                        selected={filters.desks}
                                        onChange={(selected) => handleFilterChange("desks", selected)}
                                        placeholder="Select desks"
                                        searchPlaceholder="Search desks..."
                                    />
                                </div>

                                <div className="">
                                    <Label>Agent</Label>
                                    <MultiSelect
                                        options={availableOptions.agents}
                                        selected={filters.agents}
                                        onChange={(selected) => handleFilterChange("agents", selected)}
                                        placeholder="Select agents"
                                        searchPlaceholder="Search agents..."
                                    />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Date Range Filter */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center">
                                <CalendarIcon className="h-5 w-5 mr-2" />
                                Registration Date Range
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <Label>From Date</Label>
                                    <Popover>
                                        <PopoverTrigger asChild>
                                            <Button
                                                variant="outline"
                                                className="w-full justify-start text-left font-normal"
                                            >
                                                <CalendarIcon className="mr-2 h-4 w-4" />
                                                {filters.dateFrom
                                                    ? format(filters.dateFrom, "PPP")
                                                    : "Select start date"}
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-auto p-0" align="start">
                                            <Calendar
                                                mode="single"
                                                selected={filters.dateFrom}
                                                onSelect={(date) => handleDateChange("dateFrom", date)}
                                                initialFocus
                                            />
                                        </PopoverContent>
                                    </Popover>
                                </div>
                                <div>
                                    <Label>To Date</Label>
                                    <Popover>
                                        <PopoverTrigger asChild>
                                            <Button
                                                variant="outline"
                                                className="w-full justify-start text-left font-normal"
                                            >
                                                <CalendarIcon className="mr-2 h-4 w-4" />
                                                {filters.dateTo ? format(filters.dateTo, "PPP") : "Select end date"}
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-auto p-0" align="start">
                                            <Calendar
                                                mode="single"
                                                selected={filters.dateTo}
                                                onSelect={(date) => handleDateChange("dateTo", date)}
                                                initialFocus
                                            />
                                        </PopoverContent>
                                    </Popover>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Action Buttons */}
                    <div className="flex space-x-3 pt-4">
                        <Button
                            onClick={handleApplyFilters}
                            disabled={filteredCount === 0}
                            className="flex-1 "
                        >
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Apply Filters ({filteredCount} leads)
                        </Button>
                        <Button variant="outline" onClick={handleClearFilters} className="flex-1">
                            <X className="h-4 w-4 mr-2" />
                            Clear All Filters
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
